import {
  BookOpenIcon,
  CalendarHeartIcon,
  ClockIcon,
  GraduationCapIcon,
  HandshakeIcon,
  HeartPulseIcon,
  LaptopIcon,
  MapPinIcon,
  RocketIcon,
  ShieldCheckIcon,
  SproutIcon,
  StoreIcon,
  TrendingUpIcon,
  UsersIcon,
  UtensilsIcon,
} from 'lucide-react'
import type { CompanyCard, CompanyStat } from '../types/company'
import type { Department, Job, JobType } from '../types/career'

const IMG = 'https://cdn.magicpatterns.com/patterns/generated-images/'

export const careersImages = {
  hero: `${IMG}a3ae4dd9-d087-4778-9053-816dbfe81924.jpg`,
}

export const departments: Department[] = [
  'Engineering',
  'Product & Design',
  'Sales & Partnerships',
  'Operations & Support',
  'Risk & Compliance',
  'Finance',
]

export const jobTypes: JobType[] = ['Full-time', 'Contract', 'Internship']

export const whyKayko: CompanyCard[] = [
  {
    icon: StoreIcon,
    title: 'Real impact on local businesses',
    text: 'The shops, cafés, and clinics using Kayko are down the road. You’ll see your work change how they run their day.',
  },
  {
    icon: RocketIcon,
    title: 'Learn fast, own your work',
    text: 'Small teams, clear goals, and real responsibility from your first month. We trust people to make decisions.',
  },
  {
    icon: MapPinIcon,
    title: 'Build in fintech from Kigali',
    text: 'Work on payments, lending, and business software as a regulated fintech, with the whole region in view.',
  },
  {
    icon: TrendingUpIcon,
    title: 'Grow with the company',
    text: 'As Kayko grows, so do the roles. Many of our leads started in their first job with us.',
  },
]

export const careersStats: CompanyStat[] = [
  { value: '10,000+', label: 'businesses signed up' },
  { value: '8,000+', label: 'active every month' },
  { value: 'RWF 10B+', label: 'processed each year' },
  { value: '2021', label: 'founded in Kigali' },
]

export const lifePhotos = [
  { src: `${IMG}f87eb04f-1980-4f89-81c0-9a40d840af22.jpg`, alt: 'Two engineers pair programming at a desk' },
  { src: `${IMG}13dcefca-ef10-4a7d-aa0d-0b7a7ce374e7.jpg`, alt: 'A Kayko team member showing a shop owner the app on a tablet' },
  { src: `${IMG}e4ec4ff6-3c86-4c6e-98ab-996e4f7869c5.jpg`, alt: 'A product designer leading a whiteboard session' },
  { src: `${IMG}4bb38aec-c2bb-4a95-94b0-29837fc7c9a8.jpg`, alt: 'The team sharing a meal outdoors on a team day' },
  { src: `${IMG}ed47f48b-6317-43e6-84df-60d9764e9b8a.jpg`, alt: 'Reviewing business reports on a laptop' },
]

export const careerValues: CompanyCard[] = [
  { icon: HandshakeIcon, title: 'Customers first', text: 'We visit the businesses we serve and build what they actually need.' },
  { icon: ShieldCheckIcon, title: 'Earn trust every day', text: 'We handle people’s money and records, so we’re honest, careful, and clear.' },
  { icon: SproutIcon, title: 'Keep it simple', text: 'If a shop owner can’t use it at a busy till, it isn’t finished.' },
  { icon: UsersIcon, title: 'Win together', text: 'We share context, help each other, and celebrate the team’s results.' },
]

export const benefits: CompanyCard[] = [
  { icon: HeartPulseIcon, title: 'Health insurance', text: 'Medical cover for you and your family. [to confirm]' },
  { icon: BookOpenIcon, title: 'Learning budget', text: 'An annual budget for courses, books, and certifications. [to confirm]' },
  { icon: ClockIcon, title: 'Flexible hours', text: 'Plan your day around core hours that suit your team. [to confirm]' },
  { icon: LaptopIcon, title: 'Equipment', text: 'A laptop and the tools you need to do your best work. [to confirm]' },
  { icon: CalendarHeartIcon, title: 'Team events', text: 'Quarterly team days and an annual retreat. [to confirm]' },
  { icon: GraduationCapIcon, title: 'Career development', text: 'Regular growth conversations and clear paths to lead. [to confirm]' },
  { icon: UtensilsIcon, title: 'Lunch at the office', text: 'Lunch provided on office days. [to confirm]' },
  { icon: CalendarHeartIcon, title: 'Paid time off', text: 'Annual leave plus public holidays and parental leave. [to confirm]' },
]

export const hiringSteps = [
  { title: 'Apply', text: 'Send your CV and a short note. We read every application.' },
  { title: 'Intro call', text: 'A 30-minute call to learn about you and answer your questions.' },
  { title: 'Skills interview', text: 'A practical conversation or task based on real work at Kayko.' },
  { title: 'Offer', text: 'Meet the team, then receive a clear offer within days.' },
]

export const aboutKayko =
  'Kayko Group Ltd is a Kigali-based fintech giving small businesses one platform for sales, stock, expenses, customers, reports, payments, and business financing. More than 10,000 businesses have signed up since 2021, and we process over RWF 10 billion each year.'

export const whatWeOffer = [
  'Competitive salary, reviewed every year [to confirm]',
  'Health insurance for you and your family [to confirm]',
  'Learning budget and time for growth [to confirm]',
  'Laptop and equipment',
  'A team that cares about the businesses we serve',
]

export const heardAboutOptions = ['LinkedIn', 'Kayko website', 'A friend or colleague', 'University or bootcamp', 'Job board', 'Event or meetup', 'Other']

const base = { location: 'Kigali, Rwanda', posted: '12 September 2026', applyBy: '10 October 2026' }

export const jobs: Job[] = [
  {
    ...base,
    slug: 'senior-backend-engineer',
    title: 'Senior Backend Engineer',
    department: 'Engineering',
    type: 'Full-time',
    mode: 'Hybrid',
    level: 'Senior',
    summary: 'Build the payment and ledger services that thousands of businesses rely on every day.',
    aboutRole: [
      'You’ll join the Payments team, which owns how money moves through Kayko: mobile money, card, and bank transfers, and the ledger that keeps every sale and payment matched.',
      'You’ll work closely with product, risk, and support, and help shape how we scale as more businesses join.',
    ],
    responsibilities: [
      'Design, build, and run reliable APIs and services for payments and reconciliation',
      'Improve performance and resilience for shops with unreliable connectivity',
      'Work with risk and compliance to meet regulatory requirements',
      'Review code, mentor engineers, and raise our engineering standards',
      'Take part in a fair, well-supported on-call rotation',
    ],
    requirements: [
      '5+ years building backend systems in production',
      'Strong experience with a typed backend language and relational databases',
      'Experience designing APIs and working with message queues or event-driven systems',
      'Care for correctness when handling money and personal data',
      'Clear written and spoken English; Kinyarwanda or French is a plus',
    ],
    niceToHave: [
      'Experience with mobile money or card payment integrations',
      'Background in double-entry ledgers or accounting systems',
      'Experience in a regulated financial environment',
    ],
  },
  {
    ...base,
    slug: 'product-designer',
    title: 'Product Designer',
    department: 'Product & Design',
    type: 'Full-time',
    mode: 'Hybrid',
    level: 'Mid-level',
    summary: 'Design simple, fast tools for busy shop owners — at the till and on the go.',
    aboutRole: ['You’ll design end-to-end flows across our mobile and web apps, from checkout to reports, and test them with real customers in Kigali.'],
    responsibilities: [
      'Own the design of features from research to launch',
      'Visit customers and run usability tests in shops and markets',
      'Contribute to and grow our design system',
      'Work daily with engineers and product managers',
    ],
    requirements: [
      '3+ years designing digital products',
      'A portfolio showing clear, simple interaction design',
      'Comfort presenting and defending design decisions',
    ],
    niceToHave: ['Experience designing for low-end Android devices', 'Kinyarwanda UX writing'],
  },
  {
    ...base,
    slug: 'sales-executive-smes',
    title: 'Sales Executive – SMEs',
    department: 'Sales & Partnerships',
    type: 'Full-time',
    mode: 'On-site',
    level: 'Mid-level',
    summary: 'Help shops, restaurants, and clinics across Kigali start running on Kayko.',
    aboutRole: ['You’ll meet business owners in person, understand their needs, and help them choose the right Kayko plan.'],
    responsibilities: [
      'Build a pipeline of small businesses in your area',
      'Run demos in person and by phone',
      'Onboard new customers with the support team',
      'Share customer feedback with product',
    ],
    requirements: [
      '2+ years in sales, ideally to small businesses',
      'Fluent Kinyarwanda and good English',
      'Comfortable spending most days in the field',
    ],
    niceToHave: ['Experience selling software or financial services', 'A motorbike licence'],
  },
  {
    ...base,
    slug: 'customer-success-associate',
    title: 'Customer Success Associate',
    department: 'Operations & Support',
    type: 'Full-time',
    mode: 'On-site',
    level: 'Entry-level',
    summary: 'Be the friendly voice that helps customers get the most from Kayko.',
    aboutRole: ['You’ll support customers by phone, WhatsApp, and in person, and help new businesses set up their workspace.'],
    responsibilities: [
      'Answer customer questions quickly and clearly',
      'Help new customers import products and train staff',
      'Spot common issues and share them with the team',
    ],
    requirements: ['Excellent Kinyarwanda and English', 'Patience and a genuine wish to help', 'Comfortable with smartphones and apps'],
    niceToHave: ['Experience in retail or customer service', 'French'],
  },
  {
    ...base,
    slug: 'compliance-officer',
    title: 'Compliance Officer',
    department: 'Risk & Compliance',
    type: 'Full-time',
    mode: 'On-site',
    level: 'Mid-level',
    summary: 'Keep Kayko safe, trusted, and aligned with Rwanda’s financial regulations.',
    aboutRole: ['You’ll help run our compliance programme as a licensed payment service provider and electronic money institution.'],
    responsibilities: [
      'Monitor transactions and support AML/CFT processes',
      'Prepare regulatory reports and keep policies up to date',
      'Train colleagues on compliance and data protection',
    ],
    requirements: [
      '3+ years in compliance at a bank, fintech, or MFI',
      'Good knowledge of Rwanda’s financial regulations',
      'Attention to detail and clear writing',
    ],
    niceToHave: ['A professional compliance certification', 'Experience with e-money or payments'],
  },
  {
    ...base,
    slug: 'finance-analyst',
    title: 'Finance Analyst',
    department: 'Finance',
    type: 'Full-time',
    mode: 'Hybrid',
    level: 'Mid-level',
    summary: 'Turn our numbers into insight that helps the company grow responsibly.',
    aboutRole: ['You’ll work with the CFO on reporting, budgeting, and the analysis behind our biggest decisions.'],
    responsibilities: [
      'Prepare monthly management reports',
      'Build budgets, forecasts, and business cases',
      'Support audits and tax filings',
    ],
    requirements: ['2+ years in finance or audit', 'Strong spreadsheet skills', 'A degree in finance, accounting, or economics'],
    niceToHave: ['CPA or ACCA progress', 'Experience at a startup or fintech'],
  },
]

export function getJob(slug: string): Job | undefined {
  return jobs.find((j) => j.slug === slug)
}
